import { VisualState, useKBar } from "kbar";
import { useCallback } from "react";
import { withRouter } from "react-router";
import { t } from "ttag";

import { useIsSmallScreen } from "metabase/common/hooks/use-is-small-screen";
import { getSearchTextFromLocation } from "metabase/common/search";
import type { SearchAwareLocation } from "metabase/common/search/types";
import { Button, type ButtonProps, Flex, Icon, rem } from "metabase/ui";
import { METAKEY } from "metabase/utils/browser";

import S from "./SearchButton.module.css";

type SearchButtonProps = ButtonProps & {
  location: SearchAwareLocation;
};

const SearchButtonView = ({ location, ...props }: SearchButtonProps) => {
  const kbar = useKBar();
  const { setVisualState } = kbar.query;
  const searchText = getSearchTextFromLocation(location);

  const handleClick = useCallback(() => {
    setVisualState(VisualState.showing);
  }, [setVisualState]);

  const isSmallScreen = useIsSmallScreen();

  if (isSmallScreen) {
    return (
      <Button
        h="36px"
        leftSection={<Icon name="search" />}
        variant="subtle"
        onClick={handleClick}
        color="text-secondary"
        aria-label="Search"
      />
    );
  }

  return (
    <Button
      h="36px"
      maw="240px"
      miw={rem(72)}
      flex="1 1 auto"
      c={searchText ? "text-primary" : "text-secondary"}
      leftSection={<Icon name="search" c="text-primary" />}
      onClick={handleClick}
      styles={{
        inner: {
          width: "100%",
        },
        label: {
          display: "inline-flex",
          justifyContent: "space-between",
          width: "100%",
          minWidth: 0,
        },
      }}
      className={S.searchTextButton}
      // WCAG 2.5.3 Label in Name: the accessible name must contain the
      // visible label verbatim. The button's visible content isn't just
      // "Search..." -- it also shows the Ctrl/⌘+K shortcut hint as real,
      // non-decorative text -- so a bare "Search" name doesn't contain it.
      aria-label={`${searchText || t`Search...`} ${METAKEY} ${t`K`}`}
      {...props}
    >
      {/* w="240px" used to be a hard, non-shrinking width — under real
          browser zoom high enough to squeeze the app bar's available room
          (confirmed live at 400%), this button never gave up any space,
          pushing the New/Metabot/app-switcher buttons off-screen with no
          visible way to reach them (WCAG 1.4.10 Reflow). maw (not w) plus a
          real miw floor let it shrink down to icon-plus-a-few-characters
          instead; the label itself needs its own min-width:0 + ellipsis to
          shrink cleanly rather than overflow. */}
      <span className={S.searchLabel}>{searchText || t`Search...`}</span>
      <Flex gap="xs" flex="0 0 auto">
        <span className={S.shortcutText}>{METAKEY}</span>
        <span className={S.shortcutText}>{t`K`}</span>
      </Flex>
    </Button>
  );
};

export const SearchButton = withRouter(SearchButtonView);
